/**
 * A red strip across the top of every page of a STAGING environment.
 *
 * WHY A WHOLE BANNER AND NOT JUST A LABEL. A staging environment is a faithful
 * copy of the live app: the same screens, the same shape of data, a web address
 * differing by one word, and a name differing by one bracketed suffix that
 * truncates in the switcher. Everything destructive in this product is reachable
 * from those screens, and the failure is silent in both directions. Dropping a
 * table on the live app while believing it is staging destroys real data.
 * Setting a key on staging while believing it is production leaves a real
 * outage un-fixed and looking fixed.
 *
 * None of that is a state of the app, so it cannot live in the status pill,
 * and a marker that has to be read carefully is the marker that is missed.
 *
 * Deliberately outside .shell's grid (mounted as AppShell's own first child,
 * not inside the shell div) so it never fights the rail, top bar or content,
 * exactly as ImpersonationBanner is and for the same reason. The two can stack:
 * an admin viewing as somebody else, on that person's staging environment, is
 * two separate things worth saying.
 */
import { Link, useLocation } from 'react-router';

import { useProjects } from '../../lib/auth/use-app-data';
import { liveSlugOf, sameScreenOn } from '../../lib/environments';
import { t } from '../../lib/i18n/t';
import '../../styles/components/staging-banner.css';

interface StagingBannerProps {
  slug: string | null;
}

export function StagingBanner({ slug }: StagingBannerProps) {
  const projects = useProjects();
  const location = useLocation();
  const project = slug ? projects.find((one) => one.slug === slug) : undefined;

  if (!project || project.environment !== 'staging') return null;

  // The live app this one stands in front of. The whole point of noticing you
  // are on the wrong one is being able to leave, onto the same screen.
  const liveSlug = liveSlugOf(project);

  return (
    <div className="staging-banner" role="status">
      <span className="staging-banner__tag">{t('Staging')}</span>
      <span className="staging-banner__text">
        {t('This is not the app your customers use. Changes here reach nobody.')}
      </span>
      {liveSlug !== project.slug ? (
        <Link
          className="staging-banner__exit"
          to={sameScreenOn(location.pathname, project.slug, liveSlug)}
        >
          {t('Go to the live app')}
        </Link>
      ) : null}
    </div>
  );
}
